import { useEffect, useState } from "react";

interface Props {
  scores: Record<string, number>;
}

interface SubdomainDef {
  key: string;
  label: string;
}

interface DomainDef {
  key: string;
  label: string;
  labelZh: string;
  color: string;
  bgColor: string;
  subdomains: SubdomainDef[];
}

const DOMAINS: DomainDef[] = [
  {
    key: "body",
    label: "Body",
    labelZh: "身",
    color: "hsl(142, 71%, 45%)",
    bgColor: "hsl(142, 71%, 45%, 0.1)",
    subdomains: [
      { key: "sensory_assisting", label: "Sensory Assisting" },
      { key: "somatic_regulation", label: "Somatic Regulation" },
    ],
  },
  {
    key: "vitality",
    label: "Vitality",
    labelZh: "灵",
    color: "hsl(25, 95%, 53%)",
    bgColor: "hsl(25, 95%, 53%, 0.1)",
    subdomains: [
      { key: "emotional_support", label: "Emotional Support" },
      { key: "emotional_release", label: "Emotional Release" },
      { key: "energy_alignment", label: "Energy Alignment" },
    ],
  },
  {
    key: "cognition",
    label: "Cognition",
    labelZh: "意",
    color: "hsl(271, 91%, 65%)",
    bgColor: "hsl(271, 91%, 65%, 0.1)",
    subdomains: [
      { key: "cognitive_consciousness", label: "Cognitive Consciousness" },
      { key: "subconscious_patterns", label: "Subconscious Patterns" },
    ],
  },
  {
    key: "context",
    label: "Context",
    labelZh: "境",
    color: "hsl(168, 78%, 41%)",
    bgColor: "hsl(168, 78%, 41%, 0.1)",
    subdomains: [
      { key: "relational_systems", label: "Relational Systems" },
      { key: "sociocultural_context", label: "Sociocultural Context" },
      { key: "environmental_field", label: "Environmental Field" },
    ],
  },
];

function getHealthColor(score: number): string {
  if (score <= 15) return "#9CA3AF";
  if (score <= 40) return "#22C55E";
  if (score <= 65) return "#EAB308";
  return "#EF4444";
}

function getHealthLabel(score: number): string {
  if (score <= 15) return "Not Identified";
  if (score <= 40) return "Healthy";
  if (score <= 65) return "Attention";
  return "Problem";
}

const LEGEND = [
  { color: "#9CA3AF", label: "Not Identified (0-15)" },
  { color: "#22C55E", label: "Healthy (16-40)" },
  { color: "#EAB308", label: "Attention (41-65)" },
  { color: "#EF4444", label: "Problem (66-100)" },
];

export default function StructuralRadar({ scores }: Props) {
  const [animated, setAnimated] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setAnimated(true), 100);
    return () => clearTimeout(t);
  }, [scores]);

  return (
    <div className="space-y-3 md:space-y-4">
      <h3 className="text-base md:text-lg font-semibold text-foreground flex items-center gap-2">
        <svg className="h-4 w-4 md:h-5 md:w-5 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polygon points="12,2 22,8.5 22,15.5 12,22 2,15.5 2,8.5" />
          <polygon points="12,6 18,9.5 18,14.5 12,18 6,14.5 6,9.5" />
        </svg>
        Structural Layer — Domain Hierarchy
      </h3>
      <p className="text-xs md:text-sm text-muted-foreground">
        4 domains · 10 subdomains · Color = health status
      </p>

      <div className="space-y-4 md:space-y-5">
        {DOMAINS.map((domain) => {
          const domainScores = domain.subdomains.map(s => scores[s.key] ?? 0);
          const avgScore = Math.round(domainScores.reduce((a, b) => a + b, 0) / domainScores.length);

          return (
            <div key={domain.key} className="space-y-1.5 md:space-y-2">
              {/* Domain header */}
              <div className="flex items-center gap-2">
                <span
                  className="w-2.5 h-2.5 rounded-full shrink-0"
                  style={{ backgroundColor: domain.color }}
                />
                <span className="text-xs md:text-sm font-semibold text-foreground">{domain.label}</span>
                <span
                  className="ml-auto text-[10px] md:text-xs font-medium px-1.5 md:px-2 py-0.5 rounded-full"
                  style={{
                    backgroundColor: getHealthColor(avgScore) + "20",
                    color: getHealthColor(avgScore),
                  }}
                >
                  avg {avgScore}
                </span>
              </div>

              {/* Subdomain bars */}
              <div className="space-y-1 md:space-y-1.5 pl-4 md:pl-7">
                {domain.subdomains.map((sub, si) => {
                  const score = scores[sub.key] ?? 0;
                  const healthColor = getHealthColor(score);
                  const barWidth = animated ? `${Math.max(score, 2)}%` : "2%";

                  return (
                    <div key={sub.key} className="flex items-center gap-1.5 md:gap-2">
                      <span className="text-[10px] md:text-xs text-muted-foreground w-[90px] md:w-[140px] truncate flex-shrink-0">
                        {sub.label}
                      </span>
                      <div className="flex-1 h-3 md:h-4 rounded-full bg-muted/50 overflow-hidden relative">
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: barWidth,
                            backgroundColor: animated ? healthColor : "#9CA3AF",
                            transition: `width 0.8s ease ${si * 0.1}s, background-color 0.6s ease ${si * 0.1 + 0.2}s`,
                          }}
                        />
                      </div>
                      <span
                        className="text-[10px] md:text-xs font-semibold w-6 md:w-8 text-right tabular-nums"
                        style={{
                          color: animated ? healthColor : "#9CA3AF",
                          transition: `color 0.6s ease ${si * 0.1 + 0.2}s`,
                        }}
                      >
                        {score}
                      </span>
                      <span
                        className="hidden md:inline text-[10px] w-[70px] text-right"
                        style={{
                          color: animated ? healthColor : "#9CA3AF",
                          transition: `color 0.6s ease ${si * 0.1 + 0.2}s`,
                        }}
                      >
                        {getHealthLabel(score)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-2 md:gap-3 justify-center pt-2">
        {LEGEND.map((l) => (
          <div key={l.label} className="flex items-center gap-1 md:gap-1.5 text-[10px] md:text-xs text-muted-foreground">
            <span className="inline-block w-2 h-2 md:w-2.5 md:h-2.5 rounded-full" style={{ backgroundColor: l.color }} />
            {l.label}
          </div>
        ))}
      </div>
    </div>
  );
}
